import { useCallback, useMemo, useState } from "react";

import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { FilterableProduct } from "../config";
import {
  ATTRIBUTE_CONFIGS,
  getAttributeConfig,
  PRODUCT_ATTRIBUTE_CODES,
  STRENGTH_LEVEL_LABEL_KEYS,
  STRENGTH_LEVEL_ORDER,
} from "../config";
import type {
  AttributeAggregation,
  FilterOption,
  FilterSection,
  FilterSectionConfig,
  FilterStrengthGroup,
  RawAttributeAggregations,
  SelectedAttributes,
} from "../types";
import {
  filterProductsByAttributes,
  getOptionCountsByCode,
  pruneIncompatibleSelections,
} from "../utils/attributeFilterLogic";
import { normalizeAttributeAggregations } from "../utils/normalizeAttributeAggregations";
import { getStrengthLevelMap } from "../utils/strengthLevel";

type UseProductAttributeFiltersDrawerProps = {
  attributeAggregations: RawAttributeAggregations;
  products: FilterableProduct[];
  selectedAttributes: SelectedAttributes;
  onApply: (attributes: SelectedAttributes) => void;
  onReset: () => void;
  filterSections?: FilterSectionConfig[];
};

const resolveOrderedSections = (filterSections?: FilterSectionConfig[]): FilterSectionConfig[] => {
  if (!filterSections || filterSections.length === 0) {
    return ATTRIBUTE_CONFIGS.map((config) => ({ attributeCode: config.attributeCode }));
  }

  const seenCodes = new Set<string>();

  return filterSections.filter((section) => {
    if (!section.attributeCode || seenCodes.has(section.attributeCode)) return false;

    seenCodes.add(section.attributeCode);
    return true;
  });
};

export const useProductAttributeFiltersDrawer = ({
  attributeAggregations,
  products,
  filterSections,
  selectedAttributes,
  onApply,
  onReset,
}: UseProductAttributeFiltersDrawerProps) => {
  const { __ } = useTranslations();
  const appliedAttributes = selectedAttributes;

  const [draftAttributes, setDraftAttributes] = useState<SelectedAttributes>(appliedAttributes);

  const aggregationByCode = useMemo(
    () =>
      normalizeAttributeAggregations(attributeAggregations).reduce<Record<string, AttributeAggregation>>(
        (acc, aggregation) => {
          acc[aggregation.attributeCode] = aggregation;
          return acc;
        },
        {}
      ),
    [attributeAggregations]
  );

  const optionCountsByCode = useMemo(
    () => getOptionCountsByCode(products, draftAttributes),
    [products, draftAttributes]
  );

  const strengthLevelByRating = useMemo(() => getStrengthLevelMap(products), [products]);

  const buildOption = useCallback(
    (attributeCode: string, value: string, label: string): FilterOption => {
      const selected = (draftAttributes[attributeCode] ?? []).includes(value);
      const count = optionCountsByCode[attributeCode]?.get(value) ?? 0;
      const isStrength = attributeCode === PRODUCT_ATTRIBUTE_CODES.strength;

      return {
        value,
        label,
        count,
        selected,
        // any option that would yield 0 results is disabled and greyed out.
        // A ticked option can never be in that state: toggling prunes values that stopped matching,
        // so the shopper is never left with a greyed out option they cannot untick.
        disabled: count === 0,
        rating: isStrength ? Number(value) : undefined,
      };
    },
    [draftAttributes, optionCountsByCode]
  );

  const buildStrengthGroups = useCallback(
    (aggregation: AttributeAggregation): FilterStrengthGroup[] => {
      const groupedByLevel = STRENGTH_LEVEL_ORDER.map((level) => {
        const options = aggregation.options
          .filter((option) => strengthLevelByRating[Number(option.value)] === level)
          .sort((a, b) => Number(a.value) - Number(b.value))
          .map((option) => buildOption(aggregation.attributeCode, option.value, option.label));

        return { levelLabel: __(STRENGTH_LEVEL_LABEL_KEYS[level]), options };
      });

      return groupedByLevel.filter((group) => group.options.length > 0);
    },
    [__, buildOption, strengthLevelByRating]
  );

  const sections = useMemo<FilterSection[]>(() => {
    return resolveOrderedSections(filterSections).flatMap((section) => {
      const aggregation = aggregationByCode[section.attributeCode];
      if (!aggregation || aggregation.options.length === 0) return [];

      const config = getAttributeConfig(section.attributeCode);
      const label = aggregation.label;

      if (config?.grouping === "strengthLevel") {
        const strengthGroups = buildStrengthGroups(aggregation);
        if (strengthGroups.length === 0) return [];

        return [
          {
            id: section.attributeCode,
            label,
            options: strengthGroups.flatMap((strengthGroup) => strengthGroup.options),
            strengthGroups,
          },
        ];
      }

      return [
        {
          id: section.attributeCode,
          label,
          options: aggregation.options.map((option) => buildOption(section.attributeCode, option.value, option.label)),
        },
      ];
    });
  }, [aggregationByCode, buildOption, buildStrengthGroups, filterSections]);

  const toggleOption = useCallback(
    (attributeCode: string, value: string) => {
      setDraftAttributes((prev) => {
        const current = prev[attributeCode] ?? [];
        const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];

        const updated = { ...prev };
        if (next.length > 0) {
          updated[attributeCode] = next;
        } else {
          delete updated[attributeCode];
        }

        return pruneIncompatibleSelections(products, updated, attributeCode);
      });
    },
    [products]
  );

  const applyDraft = useCallback(() => {
    onApply(draftAttributes);
  }, [draftAttributes, onApply]);

  const resetDraft = useCallback(() => {
    setDraftAttributes({});
    onReset();
  }, [onReset]);

  const syncDraftFromApplied = useCallback(() => {
    setDraftAttributes(appliedAttributes);
  }, [appliedAttributes]);

  const resultsCount = useMemo(
    () => filterProductsByAttributes(products, appliedAttributes).length,
    [products, appliedAttributes]
  );

  const activeFiltersCount = useMemo(
    () => Object.values(appliedAttributes).reduce((total, values) => total + values.length, 0),
    [appliedAttributes]
  );

  return {
    sections,
    expandedSectionId: sections[0]?.id,
    resultsCount,
    activeFiltersCount,
    toggleOption,
    applyDraft,
    resetDraft,
    syncDraftFromApplied,
  };
};
